import {
  AssignedTo,
  FormQuestionAssignmentConfigBasicDto,
  FormSectionAssignmentConfigBasicDto,
} from "@green0meter/api-client";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _, { isEmpty } from "lodash";

import { useFormAssignmentStore } from "../../FormAssignment.store";
import AssignmentDropdown from "../AssignmentDropdown";
import { getRelevantSectionsAndQuestions } from "../../FormAssignment.helpers";

import { Table, TableColumn } from "@components/common";
import { Box, Button } from "@components/base";
import { useGetFormAssignmentConfig, useUpdateFormAssignment } from "@api";
import { useUserStore } from "@store/user";

type FormQuestionAssignmentDto = FormQuestionAssignmentConfigBasicDto & {
  id: string;
};

interface SectionDetail {
  formId: string;
  section: FormSectionAssignmentConfigBasicDto;
}

const SectionDetail: FC<SectionDetail> = ({ formId, section }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [questions, setQuestions] = useState<FormQuestionAssignmentDto[]>([]);
  const {
    selectedQuestionCodes,
    selectedSections,
    setSelectedQuestionCodes,
    setSelectedSections,
    setSidePanelOpen,
  } = useFormAssignmentStore();
  const { formAssignmentConfig } = useGetFormAssignmentConfig(user, {
    formId,
  });
  const { updateFormAssignmentAsync } = useUpdateFormAssignment(user, formId);

  const handleSelectionChange = (newSelection: string[]): void => {
    const allQuestionCodes = section.formQuestions.map((q) => ({
      sectionCode: section.code,
      questionCode: q.questionCode,
    }));
    const isAllSelected = allQuestionCodes.every((q) =>
      newSelection.includes(q.questionCode),
    );
    const isNoneSelected = allQuestionCodes.every(
      (q) => !newSelection.includes(q.questionCode),
    );

    if (isAllSelected) {
      // Add all question Codes to the currently selected question Codes
      setSelectedQuestionCodes(
        _.uniq([...selectedQuestionCodes, ...allQuestionCodes]),
      );
    } else {
      // Filter out the question Codes of this section from the currently selected question Codes
      const otherSelectedQuestionCodes = selectedQuestionCodes.filter(
        (q) =>
          !allQuestionCodes.some(
            (aq) =>
              aq.questionCode === q.questionCode && aq.sectionCode === q.sectionCode,
          ),
      );

      // Add the newly selected question Codes
      setSelectedQuestionCodes(
        _.uniqBy(
          [
            ...otherSelectedQuestionCodes,
            ...newSelection.map((questionCode) => ({
              sectionCode: section.code,
              questionCode,
            })),
          ],
          (q) => `${q.sectionCode}-${q.questionCode}`,
        ),
      );
    }

    if (isNoneSelected) {
      // If section is in selectedSections, remove it
      setSelectedSections(selectedSections.filter((s) => s.id !== section.code));
    } else if (!isEmpty(newSelection)) {
      // Update sections indeterminate state
      if (selectedSections.some((s) => s.id === section.code)) {
        setSelectedSections(
          selectedSections.map((s) =>
            s.id === section.code
              ? { id: section.code, indeterminate: !isAllSelected }
              : s,
          ),
        );
      } else {
        setSelectedSections([
          ...selectedSections,
          { id: section.code, indeterminate: !isAllSelected },
        ]);
      }
    }
  };

  const handleGeneralAssignment = async (
    value: AssignedTo,
    question: FormQuestionAssignmentDto,
  ): Promise<void> => {
    if (!formId) return;

    if (isEmpty(selectedQuestionCodes)) {
      // If nothing is selected, assignment applies to the current question only
      await updateFormAssignmentAsync({
        formId,
        assignedTo: value,
        sectionCodes: [],
        questionCodes: [
          {
            sectionCode: section.code,
            questionCode: question.questionCode,
          },
        ],
      });
    } else {
      const { sectionCodes, questionCodes } = getRelevantSectionsAndQuestions(
        selectedSections,
        selectedQuestionCodes,
        formAssignmentConfig,
      );

      await updateFormAssignmentAsync({
        formId,
        assignedTo: value,
        sectionCodes: sectionCodes,
        questionCodes: questionCodes,
      });
    }
  };

  const handleCustomAssignment = (questionCode: string): void => {
    const selectedQuestion = selectedQuestionCodes.find(
      (question) => question.questionCode === questionCode,
    );

    if (!selectedQuestion) {
      // Add question to selection
      handleSelectionChange([
        ...selectedQuestionCodes.map((q) => q.questionCode),
        questionCode,
      ]);
    }

    setSidePanelOpen(true);
  };

  const columns: TableColumn<FormQuestionAssignmentDto>[] = [
    {
      title: "Question",
      key: `question-${section.code}`,
      dataKey: "name",
    },
    {
      title: "General assignment",
      key: `general-question-assignment-${section.code}`,
      tableCellProps: { width: 160, align: "center" },
      renderCellContent: (question) => (
        <AssignmentDropdown
          assignment={{
            assignedTo: question.assignedTo,
          }}
          handleChange={(item) =>
            handleGeneralAssignment(item.value as AssignedTo, question)
          }
          disabled={
            !isEmpty(selectedQuestionCodes) &&
            !selectedQuestionCodes.some(
              (q) => q.questionCode === question.questionCode,
            )
          }
        />
      ),
    },
    {
      title: "Custom assignment",
      key: `custom-question-assignment-${section.code}`,
      tableCellProps: { width: 160, align: "center" },
      renderCellContent: (question) => (
        <Button
          variant="outlined"
          onClick={() => handleCustomAssignment(question.questionCode)}
          size="extraSmall"
          disabled={
            !isEmpty(selectedQuestionCodes) &&
            !selectedQuestionCodes.some(
              (q) => q.questionCode === question.questionCode,
            )
          }
        >
          {t("formAssignment.customizeButton", "Customize")}
        </Button>
      ),
    },
  ];

  useEffect(() => {
    setQuestions(
      section.formQuestions.map((question) => ({
        ...question,
        code: question.questionCode,
        id: question.questionCode,
      })),
    );
  }, [section]);

  return (
    <Box px={0} py={3} pl={6}>
      <Table<FormQuestionAssignmentDto>
        columns={columns}
        data={questions}
        enableRowSelection
        onSelectionChange={handleSelectionChange}
        selectedRowIds={selectedQuestionCodes.map((q) => q.questionCode)}
      />
    </Box>
  );
};

export default SectionDetail;
